import { useCallback, useEffect, useState } from 'react';
import {
  colaboradoresApi,
  type ColaboradorResumo,
  type SalvarColaboradorRequest,
} from '@/features/colaboradores/api/colaboradoresApi';
import { useSyncReload } from '@/shared/hooks/useSyncReload';

type Estado = {
  colaboradores: ColaboradorResumo[];
  carregando: boolean;
  erro: string | null;
};

export function useColaboradores() {
  const [estado, setEstado] = useState<Estado>({
    colaboradores: [],
    carregando: false,
    erro: null,
  });

  const recarregar = useCallback(() => {
    setEstado((s) => ({ ...s, carregando: true, erro: null }));
    return colaboradoresApi.listar().then(
      (colaboradores) => setEstado({ colaboradores, carregando: false, erro: null }),
      (e: unknown) => {
        const mensagem = e instanceof Error ? e.message : 'Erro desconhecido';
        setEstado((s) => ({ ...s, carregando: false, erro: mensagem }));
      },
    );
  }, []);

  const atualizar = useCallback(
    async (id: string, dados: SalvarColaboradorRequest) => {
      await colaboradoresApi.atualizar(id, dados);
      void recarregar();
    },
    [recarregar],
  );

  const excluir = useCallback(
    async (id: string) => {
      setEstado((s) => ({
        ...s,
        colaboradores: s.colaboradores.filter((c) => c.id !== id),
      }));
      try {
        await colaboradoresApi.excluir(id);
      } finally {
        void recarregar();
      }
    },
    [recarregar],
  );

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  useSyncReload(recarregar);

  return { ...estado, recarregar, atualizar, excluir };
}
